/**
 * The NavLog's arithmetic and its row model — everything the table does that is
 * not React, so it can be pinned by a test without a DOM.
 *
 * Nothing here reads or writes the store: every function takes a `read(id)` and
 * returns strings in the format the LBA sheet stores. The one exception is
 * deliberate and documented — the Sicherheitshöhe, which the sheet keeps in
 * thousands of feet and a pilot compares in feet.
 */

import { LEG_NUMBERS, label, legField, type LegNumber } from '../../fields';
import { warnText, type T } from '../../i18n';
import type { FieldView } from '../../planContext';

/** Read one field's stored value; `''` when it has none. */
export type Read = (id: string) => string;

/* ---------------------------------------------------------------- *
 * Rows
 * ---------------------------------------------------------------- */

export type RowKind = 'dep' | 'leg' | 'alt' | 'total';

export interface NavRow {
  kind: RowKind;
  /** the route row this table row edits; `null` for the departure and the total */
  row: LegNumber | 'alt' | null;
  /** a stable key for React and for a test to name the row by */
  id: string;
}

/**
 * The cells that make a leg row "used". `dist` and `tc` are what the map writes,
 * the checkpoint name is what a pilot types first, and the rest cover a row
 * filled in by hand on the ground.
 */
const LEG_DATA_KEYS = ['dist', 'tc', 'safealt', 'alt', 'tas', 'wind', 'var', 'remark'] as const;

/** Does this leg row carry anything at all? */
export function legHasData(read: Read, j: LegNumber): boolean {
  if (read('cp' + (j + 1) + '_name') !== '') return true;
  return LEG_DATA_KEYS.some(k => read(legField(j, k)) !== '');
}

/** Does the alternate row carry anything? */
export function altHasData(read: Read): boolean {
  if (read('alt_name') !== '') return true;
  return LEG_DATA_KEYS.some(k => read(legField('alt', k)) !== '');
}

/**
 * Which legs the table shows: every used one, plus ONE empty row to type the
 * next checkpoint into. Twelve is the paper form's limit and therefore this
 * table's — a thirteenth leg has nowhere to be printed.
 *
 * The empty trailing row is the difference between a table and a form: it is
 * where a pilot who is not drawing on the map adds the next leg, and it costs
 * nothing when the route came from the map (the map fills rows in order). A
 * reader of a shared plan cannot type, so they do not get it — an empty row
 * offered to somebody who cannot fill it is a row that only asks a question.
 */
export function legRows(read: Read, readOnly = false): LegNumber[] {
  let last = 0;
  for (const j of LEG_NUMBERS) if (legHasData(read, j)) last = j;
  const n = Math.min(LEG_NUMBERS.length, readOnly ? last : last + 1);
  return LEG_NUMBERS.slice(0, n);
}

/** Nothing has been planned yet: no departure, no leg, no alternate. */
export function isEmptyRoute(read: Read): boolean {
  if (read('cp1_name') !== '') return false;
  if (altHasData(read)) return false;
  return !LEG_NUMBERS.some(j => legHasData(read, j));
}

/**
 * The whole table as rows: the departure, the legs, the totals, the alternate.
 *
 * The alternate sits AFTER the totals, as it does on the paper form and for the
 * same reason: it is not part of the flight the totals describe.
 */
export function rowsFor(read: Read, readOnly = false): NavRow[] {
  const rows: NavRow[] = [];
  if (!readOnly || read('cp1_name') !== '') rows.push({ kind: 'dep', row: null, id: 'dep' });
  for (const j of legRows(read, readOnly)) rows.push({ kind: 'leg', row: j, id: 'leg' + j });
  if (!readOnly || read('total_dist') !== '' || read('total_time') !== '') {
    rows.push({ kind: 'total', row: null, id: 'total' });
  }
  // an alternate is a decision, so the row is always offered — but never shown
  // empty to somebody who is only reading
  if (!readOnly || altHasData(read)) rows.push({ kind: 'alt', row: 'alt', id: 'alt' });
  return rows;
}

/* ---------------------------------------------------------------- *
 * Fuel
 * ---------------------------------------------------------------- */

/** A stored value as a number; `null` when the cell is empty or not a number. */
export function num(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * Litres per hour, read back out of the plan rather than out of the aircraft.
 *
 * `fuel_reise_l` is the cruise burn over `total_time`, written by the aircraft
 * profile whenever it knows a consumption — so the quotient is the flow the
 * FUEL BLOCK is using, and the leg column therefore always adds up to the
 * figure the pilot signs off on. Asking the profile directly would give a
 * second, subtly different number on the same screen.
 *
 * `null` when the plan has no consumption to divide (no aircraft, or one whose
 * profile has no fuel flow): then the column has nothing to say and is hidden.
 */
export function fuelPerHour(read: Read): number | null {
  const burn = num(read('fuel_reise_l'));
  const mins = num(read('total_time'));
  if (burn === null || mins === null || burn <= 0 || mins <= 0) return null;
  return (burn * 60) / mins;
}

/** Litres for one leg: the flow over the leg's own minutes. */
export function legFuelL(lph: number | null, minutes: string): number | null {
  if (lph === null) return null;
  const m = num(minutes);
  if (m === null || m < 0) return null;
  return (lph * m) / 60;
}

/**
 * Litres as the fuel block writes them: whole litres, and one decimal only
 * below ten, where the decimal is the difference between "3" and "3.4" of a
 * twenty-minute reserve.
 */
export function formatFuel(l: number | null): string {
  if (l === null) return '';
  if (l < 10) {
    const r = Math.round(l * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
  }
  return String(Math.round(l));
}

/* ---------------------------------------------------------------- *
 * Sicherheitshöhe: the sheet's unit, and the pilot's
 * ---------------------------------------------------------------- */

/** How coarse a safety altitude is: the next full hundred feet, never lower. */
const SAFEALT_STEP_FT = 100;
/** Below this a typed figure is thousands of feet, not feet (see below). */
const SHEET_UNIT_BELOW = 100;

/**
 * `2.5` (the sheet) → `2500` (what the NavLog shows and edits).
 *
 * The LBA sheet writes the Sicherheitshöhe in thousands of feet with one
 * decimal, which is fine on paper next to a column headed the same way — but in
 * the NavLog it sits two columns from Höhe/FL, and "2.5" beside "3500" is a
 * comparison nobody should have to do in their head.
 */
export function safeAltToFeet(stored: string): string {
  const n = num(stored);
  if (n === null) return stored.trim();
  return String(Math.round(n * 1000));
}

/**
 * `2500` → `2.5`, back into the unit the form and the PDF store.
 *
 * Two things make this safe to type into:
 *   · a figure below 100 is read as the SHEET's unit — nobody plans a safety
 *     altitude of 25 ft, and a pilot who knows the paper form types "2.5";
 *   · feet round UP to the next hundred. A safety altitude that rounds down is
 *     the one rounding error that matters, and the paper form's one decimal
 *     cannot hold 3250 ft anyway.
 *
 * Anything that is not a number comes back untouched, so the cell can flag it.
 */
export function feetToSafeAlt(typed: string): string {
  const v = typed.trim();
  if (v === '') return '';
  const n = num(v);
  if (n === null || n < 0) return v;
  if (n < SHEET_UNIT_BELOW) {
    const r = Math.round(n * 10) / 10;
    return Number.isInteger(r) ? String(r) : String(r);
  }
  const hundreds = Math.ceil(n / SAFEALT_STEP_FT);
  const thousands = hundreds / 10;
  return Number.isInteger(thousands) ? String(thousands) : thousands.toFixed(1);
}

/* ---------------------------------------------------------------- *
 * Totals
 * ---------------------------------------------------------------- */

export interface Totals {
  /** NM, as the sheet stores it */
  dist: string;
  /** minutes */
  time: string;
  /** hh:mm, UTC */
  eta: string;
  /** litres of cruise burn, formatted; `''` when the plan has no consumption */
  fuel: string;
}

export function totalsFor(read: Read): Totals {
  return {
    dist: read('total_dist'),
    time: read('total_time'),
    eta: read('eta'),
    fuel: formatFuel(num(read('fuel_reise_l'))),
  };
}

/* ---------------------------------------------------------------- *
 * What a cell says about itself
 * ---------------------------------------------------------------- */

/** Where a cell's value came from, in the words the Formular already uses. */
function sourceNote(t: T, f: Pick<FieldView, 'route' | 'derived' | 'derivedFrom'>): string {
  if (f.route === 's') return t('map.field.synced');
  if (f.route === 'o') return t('map.field.differs');
  // the terrain names itself with or without a corridor: the corridor is what
  // makes the suggestion checkable, its absence means a single elevation point
  if (f.derived === 'terrain') {
    return f.derivedFrom ? t('terrain.field.derived', { id: f.derivedFrom }) : t('terrain.field.elev');
  }
  // the forecast HOUR sharpens the wind's note; a plan whose meta predates it
  // still has to say the cell came from the forecast
  if (f.derived === 'winds') {
    return f.derivedFrom ? t('winds.field.derived', { id: f.derivedFrom }) : t('winds.field.derived.plain');
  }
  if (!f.derivedFrom) return '';
  if (f.derived === 'aircraft') return t('ac.field.derived', { reg: f.derivedFrom });
  if (f.derived === 'aircraft-est') return t('ac.field.derived.est', { reg: f.derivedFrom });
  if (f.derived === 'wx') return t('wx.field.derived', { id: f.derivedFrom });
  if (f.derived === 'geo') return t('geo.field.derived', { id: f.derivedFrom });
  return '';
}

export interface CellTitleOpts {
  /** the label to use instead of the field's own (the „Von" half of a leg) */
  as?: string;
  /** this column belongs to the calculator, so a typed value here is an override */
  computedColumn?: boolean;
  /** one more clause — why this cell behaves unlike the rest of its column */
  note?: string;
  /** the catalogue key of the validation message, when the value is malformed */
  invalid?: string | null;
}

/**
 * A cell's tooltip: what it is, where its value came from, and what is wrong
 * with it — in that order, in one line, and never colour alone.
 *
 * It is the same sentence the Formular shows for the same field, because two
 * views of one document that explain a value differently are two documents.
 */
export function cellTitle(t: T, id: string, f: FieldView, opts: CellTitleOpts = {}): string {
  const parts: string[] = [f.warn ? warnText(t, f.warn) : opts.as ?? label(id, t)];
  if (f.auto) parts.push(t('navlog.computed'));
  else if (opts.computedColumn && f.value !== '') parts.push(t('navlog.override'));
  const src = sourceNote(t, f);
  if (src) parts.push(src);
  if (opts.note) parts.push(opts.note);
  if (opts.invalid) parts.push(t(opts.invalid));
  return parts.join(' – ');
}
